import React, { useMemo } from "react";
import { useLoaderData } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Box } from "@mui/material";
import { styled as materialStyled } from "@mui/material/styles";
import { DateTime } from "luxon";
import SyntaxHighlighter from "react-syntax-highlighter";
import { stackoverflowDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import {
  type TestSuiteData,
  type TestSuiteLoaderApp,
  type TestSuiteRunRecordingData,
} from "../../utils/types";
import {
  capitalizeFirstLetter,
  getPromptStepsText,
  parseGptCommandsResponse,
} from "../../utils/helpers";

interface SessionInfoTabProps {
  display: boolean;
  runRecordingData: TestSuiteRunRecordingData;
}

const SessionInfoTab = ({ display, runRecordingData }: SessionInfoTabProps) => {
  const loaderData = useLoaderData() as TestSuiteLoaderApp;
  const testSuiteDataQuery = useAtomValue(loaderData.testSuiteDataAtom);
  const testSuiteData = testSuiteDataQuery.data as TestSuiteData;

  const parentRun = runRecordingData.runs[runRecordingData.runs.length - 1];

  const { cachedAiStepsCount, nonCachedAiStepsCount, localCommandsCount } =
    useMemo(() => {
      const logs = runRecordingData.recording.interactionLogs ?? [];
      let cached = 0;
      let nonCached = 0;
      let local = 0;
      for (const log of logs) {
        const { reasoning, fromCache } = parseGptCommandsResponse(
          log.gptCommands ?? []
        );
        const hasReasoning = reasoning.trim().length > 0;
        const isUserCode =
          log.appetizeCommands?.at(0)?.toLowerCase() === "user code executed";
        if (!hasReasoning || isUserCode) {
          local += 1;
        } else if (fromCache) {
          cached += 1;
        } else {
          nonCached += 1;
        }
      }
      return {
        cachedAiStepsCount: cached,
        nonCachedAiStepsCount: nonCached,
        localCommandsCount: local,
      };
    }, [runRecordingData.recording.interactionLogs]);

  return (
    <SessionInfoWrapper sx={{ display: display ? "block" : "none" }}>
      <table className="table-auto">
        <thead>
          <tr>
            <th
              className="text-left pr-4 whitespace-nowrap"
              style={{ width: "240px" }}
            >
              Property
            </th>
            <th className="text-left">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">Status</td>
            <td>{parentRun.runData?.status}</td>
          </tr>
          {parentRun.startedTimeInEpoch != null && (
            <tr>
              <td className="pr-4 whitespace-nowrap align-top">Started at</td>
              <td>
                {DateTime.fromMillis(
                  parentRun.startedTimeInEpoch * 1000
                ).toFormat("yyyy-MM-dd hh:mm a")}
              </td>
            </tr>
          )}
          {parentRun.runData?.finishedTimeInEpoch != null && (
            <tr>
              <td className="pr-4 whitespace-nowrap align-top">Finished at</td>
              <td>
                {DateTime.fromMillis(
                  parentRun.runData.finishedTimeInEpoch * 1000
                ).toFormat("yyyy-MM-dd hh:mm a")}
              </td>
            </tr>
          )}
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">AI Steps</td>
            <td>{nonCachedAiStepsCount}</td>
          </tr>
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">
              Cached AI Steps
            </td>
            <td>{cachedAiStepsCount}</td>
          </tr>
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">Local Commands</td>
            <td>{localCommandsCount}</td>
          </tr>
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">Locale</td>
            <td>{parentRun.locale}</td>
          </tr>
          {parentRun.runData?.coordinates?.latitude != null &&
            parentRun.runData?.coordinates?.longitude && (
              <tr>
                <td className="pr-4 whitespace-nowrap align-top">Position</td>
                <td>
                  {parentRun.runData.coordinates.latitude},
                  {parentRun.runData.coordinates.longitude}
                </td>
              </tr>
            )}
          {testSuiteData.appVersionCode != null && (
            <tr>
              <td className="pr-4 whitespace-nowrap align-top">
                App Version Code
              </td>
              <td>{testSuiteData.appVersionCode}</td>
            </tr>
          )}
          {testSuiteData.appVersionName != null && (
            <tr>
              <td className="pr-4 whitespace-nowrap align-top">
                App Version Name
              </td>
              <td>{testSuiteData.appVersionName}</td>
            </tr>
          )}
          {testSuiteData.app_name != null && (
            <tr>
              <td className="pr-4 whitespace-nowrap align-top">App Name</td>
              <td>{testSuiteData.app_name}</td>
            </tr>
          )}
          {testSuiteData.bundle != null && (
            <tr>
              <td className="pr-4 whitespace-nowrap align-top">App Bundle</td>
              <td>{testSuiteData.bundle}</td>
            </tr>
          )}
          {parentRun.platform != null && (
            <tr>
              <td className="pr-4 whitespace-nowrap align-top">Platform</td>
              <td>{capitalizeFirstLetter(parentRun.platform)}</td>
            </tr>
          )}
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">OS</td>
            <td>{capitalizeFirstLetter(parentRun.os)}</td>
          </tr>
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">Device</td>
            <td>{capitalizeFirstLetter(parentRun.device)}</td>
          </tr>
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">Meta Data</td>
            <td>
              {testSuiteData &&
                Object.keys(testSuiteData.metaData).length > 0 && (
                  <SyntaxHighlighter
                    language="json"
                    style={stackoverflowDark}
                    customStyle={{
                      width: "100%",
                    }}
                  >
                    {JSON.stringify(testSuiteData.metaData, null, 2)}
                  </SyntaxHighlighter>
                )}
              {testSuiteData &&
                Object.keys(testSuiteData.metaData).length === 0 && (
                  <p>-</p>
                )}
            </td>
          </tr>
          <tr>
            <td className="pr-4 whitespace-nowrap align-top">Prompt</td>
            <td>
              {runRecordingData.runs.map((run, index) => {
                const overridePrompt =
                  run.runData?.reviewLoopOverrideGlobalTestTask;
                const latestPromptText =
                  overridePrompt != null && overridePrompt.length > 0
                    ? overridePrompt
                    : getPromptStepsText(run.promptSteps);
                return (
                  <div className={"flex flex-col"} key={index}>
                    <p
                      className={"font-bold"}
                      style={{ lineBreak: "anywhere" }}
                    >
                      {`Test #${index + 1}`} - {run.runData?.title}
                    </p>
                    {latestPromptText.split("\n").map((text, textIndex) => (
                      <p key={textIndex}>{text}</p>
                    ))}
                  </div>
                );
              })}
            </td>
          </tr>
        </tbody>
      </table>
    </SessionInfoWrapper>
  );
};

const SessionInfoWrapper = materialStyled(Box)({
  gridArea: "tab-content",
  padding: "16px",
  margin: "16px",
  overflow: "auto",
  height: "100%",
  borderRadius: "4px",
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  boxShadow:
    "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
  "& table th, & table td": {
    padding: "4px",
  },
});

export default SessionInfoTab;
